import { exhaustGasCleaningSystemTypeOptions } from "@features/vessel-nomination/add/constants";
import type { Parcel, VesselNominationDetailsResponse } from "@services/vessel-nomination/types";
import { convertUtcToUae, formatDateToDDMMYYYY } from "@utils/date-time";
import { t } from "i18next";
import { LANG_BASE, TEST_ID_BASE } from "../VesselNominationDetails";

const LANG_BASE_PAQ = `${LANG_BASE}.paq`;

export type PaqItem = {
  testId: string;
  label: string;
  value?: string;
  tableValues?: Parcel[];
  unitOfMeasure?: string;
  rowSpan?: 2;
  colStart?: 1 | 2;
};

export type PaqSection = {
  title: string;
  testId: string;
  isTable?: boolean;
  items: PaqItem[];
};

const formatPaqValue = (value: boolean | number | string | null | undefined) => {
  if (value === null || value === undefined || value === "") return "-";
  if (value === "NONE") return "N/A";
  return String(value);
};

const formatBooleanValue = (value: boolean | null | undefined) => {
  if (value === null || value === undefined) return "N/A";
  return value ? "Yes" : "No";
};

const formatReferenceValue = (
  value: string | null | undefined,
  options: { label: string; value: string }[],
) => {
  const formattedValue = formatPaqValue(value);
  if (formattedValue === "-") return formattedValue;

  return options.find((option) => option.value === value)?.label ?? formattedValue;
};

const formatSecurityLevel = (value: string | null | undefined) => {
  const match = value?.match(/^LEVEL_(\d+)$/);

  if (!match) return formatPaqValue(value);

  return `Level ${match[1]}`;
};

const formatVesselCondition = (
  vesselCondition:
    | NonNullable<VesselNominationDetailsResponse["cargoOnboard"]>["vesselCondition"]
    | string
    | null
    | undefined,
) => {
  if (typeof vesselCondition === "string") {
    return formatPaqValue(vesselCondition);
  }

  return formatPaqValue(vesselCondition?.conditionDesc);
};

export const mapPaqDetails = (
  nominationDetails: VesselNominationDetailsResponse | undefined,
): PaqSection[] => [
  {
    title: t(`${LANG_BASE_PAQ}.call-sign-details`),
    testId: `${TEST_ID_BASE}-call-sign-details`,
    items: [
      {
        testId: `${TEST_ID_BASE}-requested-time-of-arrival`,
        label: t(`${LANG_BASE_PAQ}.requested-time-of-arrival`),
        value: nominationDetails?.rta
          ? formatDateToDDMMYYYY(convertUtcToUae(nominationDetails?.rta))
          : "-",
      },
      {
        testId: `${TEST_ID_BASE}-call-sign`,
        label: t(`${LANG_BASE_PAQ}.call-sign`),
        value: formatPaqValue(nominationDetails?.callSign),
      },
      {
        testId: `${TEST_ID_BASE}-previous-vessel-name`,
        label: t(`${LANG_BASE_PAQ}.previous-vessel-name`),
        value: formatPaqValue(nominationDetails?.previousVesselName),
      },
    ],
  },
  {
    title: t(`${LANG_BASE_PAQ}.drafts`),
    testId: `${TEST_ID_BASE}-drafts`,
    items: [
      {
        testId: `${TEST_ID_BASE}-arrival-draft`,
        label: t(`${LANG_BASE_PAQ}.arrival-draft`),
        value: formatPaqValue(nominationDetails?.arrivalDraft),
        unitOfMeasure: "m",
      },
      {
        testId: `${TEST_ID_BASE}-departure-draft`,
        label: t(`${LANG_BASE_PAQ}.departure-draft`),
        value: formatPaqValue(nominationDetails?.departureDraft),
        unitOfMeasure: "m",
      },
    ],
  },
  {
    title: t(`${LANG_BASE_PAQ}.arrival-draught`),
    testId: `${TEST_ID_BASE}-arrival-draught`,
    items: [
      {
        testId: `${TEST_ID_BASE}-arrival-fwd`,
        label: t(`${LANG_BASE_PAQ}.fwd`),
        value: formatPaqValue(nominationDetails?.arrivalDraught?.fwd),
        unitOfMeasure: "m",
      },
      {
        testId: `${TEST_ID_BASE}-arrival-mid`,
        label: t(`${LANG_BASE_PAQ}.mid`),
        value: formatPaqValue(nominationDetails?.arrivalDraught?.mid),
        unitOfMeasure: "m",
      },
      {
        testId: `${TEST_ID_BASE}-arrival-aft`,
        label: t(`${LANG_BASE_PAQ}.aft`),
        value: formatPaqValue(nominationDetails?.arrivalDraught?.aft),
        unitOfMeasure: "m",
      },
      {
        testId: `${TEST_ID_BASE}-arrival-max-trim`,
        label: t(`${LANG_BASE_PAQ}.max-trim`),
        value: formatPaqValue(nominationDetails?.arrivalDraught?.maxTrimExpectedDuringOperations),
        unitOfMeasure: "m",
      },
    ],
  },
  {
    title: t(`${LANG_BASE_PAQ}.departure-draught`),
    testId: `${TEST_ID_BASE}-departure-draught`,
    items: [
      {
        testId: `${TEST_ID_BASE}-departure-fwd`,
        label: t(`${LANG_BASE_PAQ}.fwd`),
        value: formatPaqValue(nominationDetails?.departureDraught?.fwd),
        unitOfMeasure: "m",
      },
      {
        testId: `${TEST_ID_BASE}-departure-mid`,
        label: t(`${LANG_BASE_PAQ}.mid`),
        value: formatPaqValue(nominationDetails?.departureDraught?.mid),
        unitOfMeasure: "m",
      },
      {
        testId: `${TEST_ID_BASE}-departure-aft`,
        label: t(`${LANG_BASE_PAQ}.aft`),
        value: formatPaqValue(nominationDetails?.departureDraught?.aft),
        unitOfMeasure: "m",
      },
    ],
  },
  {
    title: t(`${LANG_BASE_PAQ}.displacement`),
    testId: `${TEST_ID_BASE}-displacement`,
    items: [
      {
        testId: `${TEST_ID_BASE}-light`,
        label: t(`${LANG_BASE_PAQ}.light`),
        value: formatPaqValue(nominationDetails?.displacement?.light),
        unitOfMeasure: "MT",
      },
      {
        testId: `${TEST_ID_BASE}-loaded`,
        label: t(`${LANG_BASE_PAQ}.loaded`),
        value: formatPaqValue(nominationDetails?.displacement?.loaded),
        unitOfMeasure: "MT",
      },
      {
        testId: `${TEST_ID_BASE}-arrival`,
        label: t(`${LANG_BASE_PAQ}.arrival`),
        value: formatPaqValue(nominationDetails?.displacement?.arrival),
        unitOfMeasure: "MT",
      },
      {
        testId: `${TEST_ID_BASE}-departure`,
        label: t(`${LANG_BASE_PAQ}.departure`),
        value: formatPaqValue(nominationDetails?.displacement?.departure),
        unitOfMeasure: "MT",
      },
    ],
  },
  {
    title: t(`${LANG_BASE_PAQ}.tonnage`),
    testId: `${TEST_ID_BASE}-tonnage`,
    items: [
      {
        testId: `${TEST_ID_BASE}-gross-tonnage`,
        label: t(`${LANG_BASE_PAQ}.gross-tonnage`),
        value: formatPaqValue(nominationDetails?.grossTonnage),
        unitOfMeasure: "T",
      },
      {
        testId: `${TEST_ID_BASE}-net-tonnage`,
        label: t(`${LANG_BASE_PAQ}.net-tonnage`),
        value: formatPaqValue(nominationDetails?.netTonnage),
        unitOfMeasure: "T",
      },
    ],
  },
  {
    title: t(`${LANG_BASE_PAQ}.dead-weight`),
    testId: `${TEST_ID_BASE}-dead-weight`,
    items: [
      {
        testId: `${TEST_ID_BASE}-summer`,
        label: t(`${LANG_BASE_PAQ}.summer`),
        value: formatPaqValue(nominationDetails?.deadWeight?.summerDwt),
        unitOfMeasure: "MT",
      },
      {
        testId: `${TEST_ID_BASE}-tropical`,
        label: t(`${LANG_BASE_PAQ}.tropical`),
        value: formatPaqValue(nominationDetails?.deadWeight?.tropicalDwt),
        unitOfMeasure: "MT",
      },
    ],
  },
  {
    title: t(`${LANG_BASE_PAQ}.manifolds`),
    testId: `${TEST_ID_BASE}-manifolds`,
    items: [
      {
        testId: `${TEST_ID_BASE}-size`,
        label: t(`${LANG_BASE_PAQ}.size`),
        value: formatPaqValue(nominationDetails?.manifolds.size),
        unitOfMeasure: "in",
      },
      {
        testId: `${TEST_ID_BASE}-number`,
        label: t(`${LANG_BASE_PAQ}.number`),
        value: formatPaqValue(nominationDetails?.manifolds.numberOfManifolds),
      },
      {
        testId: `${TEST_ID_BASE}-manifolds-distance`,
        label: t(`${LANG_BASE_PAQ}.manifolds-distance`),
        value: formatPaqValue(nominationDetails?.manifolds.distanceBetweenCenterOfManifolds),
        unitOfMeasure: "m",
      },
      {
        testId: `${TEST_ID_BASE}-manifolds-height-grafting`,
        label: t(`${LANG_BASE_PAQ}.manifolds-height-grafting`),
        value: formatPaqValue(nominationDetails?.manifolds.manifoldHeightFromGrating),
        unitOfMeasure: "m",
      },
      {
        testId: `${TEST_ID_BASE}-manifolds-height-ballast`,
        label: t(`${LANG_BASE_PAQ}.manifolds-height-ballast`),
        value: formatPaqValue(nominationDetails?.manifolds.manifoldHeightAboveWaterlineBallast),
        unitOfMeasure: "m",
      },
      {
        testId: `${TEST_ID_BASE}-manifolds-height-loaded`,
        label: t(`${LANG_BASE_PAQ}.manifolds-height-loaded`),
        value: formatPaqValue(nominationDetails?.manifolds.manifoldHeightAboveWaterlineLoaded),
        unitOfMeasure: "m",
      },
      {
        testId: `${TEST_ID_BASE}-manifolds-extreme-height`,
        label: t(`${LANG_BASE_PAQ}.manifolds-extreme-height`),
        value: formatPaqValue(
          nominationDetails?.manifolds.manifoldExtremeHeightAboveWaterlineDuringOperations,
        ),
        unitOfMeasure: "m",
      },
      {
        testId: `${TEST_ID_BASE}-manifolds-distance-bow-bcm-stern`,
        label: t(`${LANG_BASE_PAQ}.manifolds-distance-bow-bcm-stern`),
        value: formatPaqValue(nominationDetails?.manifolds.distanceBowToManifoldToSternBcm),
        unitOfMeasure: "m",
      },
      {
        testId: `${TEST_ID_BASE}-manifolds-distance-bow-scm-stern`,
        label: t(`${LANG_BASE_PAQ}.manifolds-distance-bow-scm-stern`),
        value: formatPaqValue(nominationDetails?.manifolds.distanceBowToManifoldToSternScm),
        unitOfMeasure: "m",
      },
      {
        testId: `${TEST_ID_BASE}-manifolds-distance-inboard`,
        label: t(`${LANG_BASE_PAQ}.manifolds-distance-inboard`),
        value: formatPaqValue(
          nominationDetails?.manifolds.distanceInboardFromManifoldToShipsSideRail,
        ),
        unitOfMeasure: "m",
      },
      {
        testId: `${TEST_ID_BASE}-terminal-loading-arm-connection`,
        label: t(`${LANG_BASE_PAQ}.terminal-loading-arm-connection`),
        value: formatBooleanValue(
          nominationDetails?.manifolds.terminalLoadingArmConnectionSuitable,
        ),
        rowSpan:
          nominationDetails?.manifolds.exhaustGasCleaningSystemFitted === true ? 2 : undefined,
      },
      {
        testId: `${TEST_ID_BASE}-exhaust-gas-cleaning-system`,
        label: t(`${LANG_BASE_PAQ}.exhaust-gas-cleaning-system`),
        value: formatBooleanValue(nominationDetails?.manifolds.exhaustGasCleaningSystemFitted),
      },
      ...(nominationDetails?.manifolds.exhaustGasCleaningSystemFitted === true
        ? [
            {
              testId: `${TEST_ID_BASE}-exhaust-gas-cleaning-system-type`,
              label: t(`${LANG_BASE_PAQ}.exhaust-gas-cleaning-system-type`),
              value: formatReferenceValue(
                nominationDetails?.manifolds.exhaustGasCleaningSystemType,
                exhaustGasCleaningSystemTypeOptions,
              ),
            },
          ]
        : []),
      {
        testId: `${TEST_ID_BASE}-confirm-inert-gas-system-fitted`,
        label: t(`${LANG_BASE_PAQ}.confirm-inert-gas-system-fitted`),
        value: formatBooleanValue(nominationDetails?.manifolds.inertGasSystemFitted),
      },
      {
        testId: `${TEST_ID_BASE}-is-inert-gas-system-operational`,
        label: t(`${LANG_BASE_PAQ}.is-inert-gas-system-operational`),
        value: formatBooleanValue(
          nominationDetails?.manifolds.cotsInInertConditionAndSystemOperational,
        ),
      },
    ],
  },
  {
    title: t(`${LANG_BASE_PAQ}.cargo-onboard`),
    testId: `${TEST_ID_BASE}-cargo-onboard`,
    items: [
      {
        testId: `${TEST_ID_BASE}-vessel-condition`,
        label: t(`${LANG_BASE_PAQ}.vessel-condition`),
        value: formatVesselCondition(nominationDetails?.cargoOnboard?.vesselCondition),
      },
      {
        testId: `${TEST_ID_BASE}-cargo-type`,
        label: t(`${LANG_BASE_PAQ}.cargo-type`),
        value: formatPaqValue(
          nominationDetails?.cargoOnboard?.cargoType?.cargoDescription ??
            nominationDetails?.cargoOnboard?.cargoTypeOther?.cargoDescription ??
            nominationDetails?.cargoOnboard?.cargoTypeOther?.cargoCode,
        ),
      },
      {
        testId: `${TEST_ID_BASE}-cargo-quantity-m3`,
        label: t(`${LANG_BASE_PAQ}.cargo-quantity-m3`),
        value: formatPaqValue(nominationDetails?.cargoOnboard?.cargoQuantityM3Gov),
        unitOfMeasure: "QM",
      },
      {
        testId: `${TEST_ID_BASE}-cargo-quantity`,
        label: t(`${LANG_BASE_PAQ}.cargo-quantity`),
        value: formatPaqValue(nominationDetails?.cargoOnboard?.cargoQuantityMt),
        unitOfMeasure: "MT",
      },
    ],
  },
  {
    title: t(`${LANG_BASE_PAQ}.cargo-type-and-quantity`),
    testId: `${TEST_ID_BASE}-cargo-type-and-quantity`,
    isTable: true,
    items: [
      {
        testId: `${TEST_ID_BASE}-cargo-type-and-quantity-details`,
        label: t(`${LANG_BASE_PAQ}.cargo-type-and-quantity-details`),
        tableValues: nominationDetails?.parcels,
      },
    ],
  },
  {
    title: t(`${LANG_BASE_PAQ}.toxic-component`),
    testId: `${TEST_ID_BASE}-toxic-component`,
    items: [
      {
        testId: `${TEST_ID_BASE}-flash-point`,
        label: t(`${LANG_BASE_PAQ}.flash-point`),
        value: formatPaqValue(nominationDetails?.toxicComponentDetails?.flashPoint),
        unitOfMeasure: "°C",
      },
      {
        testId: `${TEST_ID_BASE}-sulphur`,
        label: t(`${LANG_BASE_PAQ}.sulphur`),
        value: formatPaqValue(nominationDetails?.toxicComponentDetails?.sulphurWtPercent),
        unitOfMeasure: "%",
      },
      {
        testId: `${TEST_ID_BASE}-mercaptan`,
        label: t(`${LANG_BASE_PAQ}.mercaptan`),
        value: formatPaqValue(nominationDetails?.toxicComponentDetails?.mercaptan),
        unitOfMeasure: "ppm",
      },
      {
        testId: `${TEST_ID_BASE}-stowed-cargo-temperature`,
        label: t(`${LANG_BASE_PAQ}.stowed-cargo-temperature`),
        value: formatPaqValue(nominationDetails?.toxicComponentDetails?.stowedCargoTemperature),
        unitOfMeasure: "°C",
      },
      {
        testId: `${TEST_ID_BASE}-hydrogen-sulphide`,
        label: t(`${LANG_BASE_PAQ}.hydrogen-sulphide`),
        value: formatPaqValue(nominationDetails?.toxicComponentDetails?.hydrogenSulphidePpm),
        unitOfMeasure: "ppm",
      },
      {
        testId: `${TEST_ID_BASE}-rvp`,
        label: t(`${LANG_BASE_PAQ}.rvp`),
        value: formatPaqValue(nominationDetails?.toxicComponentDetails?.rvp),
        unitOfMeasure: "psi",
      },
      {
        testId: `${TEST_ID_BASE}-colour-astm`,
        label: t(`${LANG_BASE_PAQ}.colour-astm`),
        value: formatPaqValue(nominationDetails?.toxicComponentDetails?.colourAstm),
      },
      {
        testId: `${TEST_ID_BASE}-benzene-content`,
        label: t(`${LANG_BASE_PAQ}.benzene-content`),
        value: formatPaqValue(nominationDetails?.toxicComponentDetails?.benzeneContentVolPercent),
        unitOfMeasure: "%",
      },
      {
        testId: `${TEST_ID_BASE}-are-high-h2s-tanks-adequate`,
        label: t(`${LANG_BASE_PAQ}.are-high-h2s-tanks-adequate`),
        value: formatBooleanValue(nominationDetails?.toxicComponentDetails?.highH2sTanksPurged),
      },
      {
        testId: `${TEST_ID_BASE}-avg-pressure-cargo-tanks-arrival`,
        label: t(`${LANG_BASE_PAQ}.avg-pressure-cargo-tanks-arrival`),
        value: formatPaqValue(
          nominationDetails?.toxicComponentDetails?.avgPressureOfCargoTanksOnArrival,
        ),
        unitOfMeasure: "mmWG",
      },
      {
        testId: `${TEST_ID_BASE}-max-pressure-manifolds`,
        label: t(`${LANG_BASE_PAQ}.max-pressure-manifolds`),
        value: formatPaqValue(
          nominationDetails?.toxicComponentDetails?.maximumAcceptablePressureAtShipsManifolds,
        ),
        unitOfMeasure: "bar",
      },
      {
        testId: `${TEST_ID_BASE}-is-crude-oil-washing-operation-planned`,
        label: t(`${LANG_BASE_PAQ}.is-crude-oil-washing-operation-planned`),
        value: formatBooleanValue(nominationDetails?.toxicComponentDetails?.crudeOilWashingPlanned),
      },
    ],
  },
  {
    title: t(`${LANG_BASE_PAQ}.mooring`),
    testId: `${TEST_ID_BASE}-mooring`,
    items: [
      {
        testId: `${TEST_ID_BASE}-mooring-fwd`,
        label: t(`${LANG_BASE_PAQ}.fwd`),
        value: formatPaqValue(nominationDetails?.mooringDetails?.fwd),
        unitOfMeasure: "m",
      },
      {
        testId: `${TEST_ID_BASE}-mooring-mid`,
        label: t(`${LANG_BASE_PAQ}.mid`),
        value: formatPaqValue(nominationDetails?.mooringDetails?.mid),
        unitOfMeasure: "m",
      },
      {
        testId: `${TEST_ID_BASE}-mooring-aft`,
        label: t(`${LANG_BASE_PAQ}.aft`),
        value: formatPaqValue(nominationDetails?.mooringDetails?.aft),
        unitOfMeasure: "m",
      },
      {
        testId: `${TEST_ID_BASE}-mooring-details-and-deficiencies`,
        label: t(`${LANG_BASE_PAQ}.mooring-details-and-deficiencies`),
        value: formatPaqValue(nominationDetails?.mooringDetails?.mooringArrangementDeficiencies),
        colStart: 1,
      },
      {
        testId: `${TEST_ID_BASE}-any-defects-hull-machinery-equipment`,
        label: t(`${LANG_BASE_PAQ}.any-defects-hull-machinery-equipment`),
        value: formatPaqValue(nominationDetails?.mooringDetails?.defectsAffectingSafeOperations),
      },
      {
        testId: `${TEST_ID_BASE}-area-with-protrusion`,
        label: t(`${LANG_BASE_PAQ}.area-with-protrusion`),
        value: formatPaqValue(nominationDetails?.mooringDetails?.hullProtrusionArea),
      },
    ],
  },
  {
    title: t(`${LANG_BASE_PAQ}.last-sire-inspection`),
    testId: `${TEST_ID_BASE}-last-sire-inspection`,
    items: [
      {
        testId: `${TEST_ID_BASE}-date`,
        label: t(`${LANG_BASE_PAQ}.date`),
        value: nominationDetails?.inspectionDetails.lastSireInspectionDate
          ? formatDateToDDMMYYYY(
              convertUtcToUae(nominationDetails?.inspectionDetails.lastSireInspectionDate),
            )
          : "-",
      },
      {
        testId: `${TEST_ID_BASE}-issuing-authority`,
        label: t(`${LANG_BASE_PAQ}.issuing-authority`),
        value: formatPaqValue(
          nominationDetails?.inspectionDetails.lastSireInspectionIssuingAuthority,
        ),
      },
    ],
  },
  {
    title: t(`${LANG_BASE_PAQ}.last-psc-inspection`),
    testId: `${TEST_ID_BASE}-last-psc-inspection`,
    items: [
      {
        testId: `${TEST_ID_BASE}-date`,
        label: t(`${LANG_BASE_PAQ}.date`),
        value: nominationDetails?.inspectionDetails.lastPscInspectionDate
          ? formatDateToDDMMYYYY(
              convertUtcToUae(nominationDetails?.inspectionDetails.lastPscInspectionDate),
            )
          : "-",
      },
      {
        testId: `${TEST_ID_BASE}-place`,
        label: t(`${LANG_BASE_PAQ}.place`),
        value: formatPaqValue(nominationDetails?.inspectionDetails.lastPscInspectionPlace),
      },
    ],
  },
  {
    title: t(`${LANG_BASE_PAQ}.voyage-information`),
    testId: `${TEST_ID_BASE}-voyage-information`,
    items: [
      {
        testId: `${TEST_ID_BASE}-last-port-of-call`,
        label: t(`${LANG_BASE_PAQ}.last-port-of-call`),
        value: formatPaqValue(nominationDetails?.voyageInformation?.lastPortOfCallCode?.portName),
      },
      {
        testId: `${TEST_ID_BASE}-next-port-of-call`,
        label: t(`${LANG_BASE_PAQ}.next-port-of-call`),
        value: formatPaqValue(nominationDetails?.voyageInformation?.nextPortOfCallCode?.portName),
      },
      {
        testId: `${TEST_ID_BASE}-security-level`,
        label: t(`${LANG_BASE_PAQ}.security-level`),
        value: formatSecurityLevel(nominationDetails?.voyageInformation?.securityLevel),
      },
    ],
  },
];
